<script setup lang="ts">
const { t: $t } = useI18n()

import { z } from "zod";
import { type IOurRecaptchaComposition, useVueRecaptcha } from "~/composables/useVueRecaptcha";
import type { FormState } from "~/models/forms";

const { locale, t } = useI18n();
const route = useRoute();

definePageMeta({
  layout: "anonymous",

  auth: {
    anonymousOnly: true,
    redirectAuthenticatedTo: "/",
  },
});

const querySchema = z.object({
  email: z.string().max(100).optional(),
});

const { success: queryDataIsValid, data: validQueryData } = querySchema.safeParse(route.query);

useHead({
  title: () => t("reset-request.title"),
});

const { data: cmsData } = await useFetch("/api/cms/account-page", {
  query: { locale: locale },
});

const state = reactive({
  email: queryDataIsValid ? validQueryData.email : undefined,
});

const formValidationEmail = t("form.validation.email");
const formValidationRequired = t("form.validation.required");

const formSchema = z.object({
  email: z.string({ required_error: formValidationRequired }).email(formValidationEmail),
});

const form = ref(); // remove?
const formState = ref<FormState>("mounting");
const loadingIndicator = useLoadingIndicator();
const gtm = useGtmTracking();

let recaptcha: IOurRecaptchaComposition | undefined;

async function onSubmit() {
  formState.value = "pending";
  loadingIndicator.start();

  try {
    const body = { ...unref(state) };

    const recaptchaToken = (recaptcha && await recaptcha.execute("request_reset")) || "";
    await $fetch("/api/ecom/account/request-reset", {
      method: "POST",
      body: body,
      headers: { "X-Recaptcha-Token": recaptchaToken },
    });

    gtm.trackCustomEvent("password-reset-request");

    formState.value = "success";
    document.body.scrollIntoView({ behavior: "smooth" });
  }
  catch (error: any) {
    formState.value = "error";
    useFailToast(t("common.title.error"), error.toString());
  }
  finally {
    loadingIndicator.finish();
  }
}

const title = computed(() =>
  formState.value === "success" && cmsData.value
    ? cmsData.value.resetRequestConfirmationTitle
    : $t("reset-request.title"),

);

onMounted(async () => {
  recaptcha = await useVueRecaptcha();
  formState.value = "idle";
});
</script>

<template>
  <CenterCard :title="title">
    <template v-if="formState !== 'success'">
      <div class="mb-4xl">
        <div>{{ $t('reset-request.instruction.1') }}</div>
        <div>{{ $t('reset-request.instruction.2') }}</div>
      </div>

      <UForm
        ref="form" :schema="formSchema" :state="state"
        aria-live="assertive" role="form" @submit="onSubmit"
      >
        <UFormGroup :label="t('reset-request.label.email')" required>
          <UInput
            v-model="state.email" required type="email" autocomplete="email"
            inputmode="email" spellcheck="false" data-1p-ignore
          />
        </UFormGroup>

        <UButton
          type="submit"
          :loading="formState === 'pending'"
          :disabled="formState === 'mounting'"
          class="mb-4xl w-full justify-center sm:w-auto sm:justify-start"
        >
          {{ $t("reset-request.action.submit") }}
        </UButton>

        <AlreadyAnAccount />
      </UForm>
    </template>
    <template v-if="formState === 'success' && cmsData">
      <div class="confirmation-text" v-html="cmsData.resetRequestConfirmationText" />
    </template>
  </CenterCard>
</template>
